import React, { useEffect, useState } from 'react';
import { useSurveyStore } from '../../surveyutilities/surveyStore';

export function useQuestionProperties(questionId) {
    const question = useSurveyStore(
        (state) => state.questions.find((q) => q.internalId === questionId) || null
    );
    const updateQuestion = useSurveyStore((state) => state.updateQuestion);
    const validateName = useSurveyStore((state) => state.validateName);
    const [nameError, setNameError] = useState('');
    const [localName, setLocalName] = useState(question?.name || '');

    useEffect(() => {
        setLocalName(question?.name || '');
    }, [question?.name]);

    const handleUpdate = (property, value) => {
        updateQuestion(questionId, { [property]: value });
    };

    const handleNameChange = (value) => {
        setLocalName(value);
        const validation = validateName(value, questionId);
        setNameError(validation.error);
        if (validation.isValid) {
            updateQuestion(questionId, { name: value });
        }
    };

    return {
        question,
        localName,
        nameError,
        handleUpdate,
        handleNameChange,
        updateQuestion,
    };
}

export function PropsSection({ title, children }) {
    return (
        <div className="ecx-survey-props__section">
            {title ? <div className="ecx-survey-props__section-title">{title}</div> : null}
            <div className="ecx-survey-props__fields">{children}</div>
        </div>
    );
}

export function PropsField({ label, hint, error, children }) {
    return (
        <div className="ecx-survey-props__field">
            {label ? <label className="ecx-survey-props__label">{label}</label> : null}
            {children}
            {error ? <div className="ecx-survey-props__error">{error}</div> : null}
            {!error && hint ? <div className="ecx-survey-props__hint">{hint}</div> : null}
        </div>
    );
}

export function PropsToggle({ checked, onChange, label }) {
    return (
        <label className="ecx-survey-props__toggle">
            <input
                type="checkbox"
                className="form-check-input"
                role="switch"
                checked={!!checked}
                onChange={onChange}
            />
            <span>{label}</span>
        </label>
    );
}

export function PropsCommonFields({
    localName,
    nameError,
    onNameChange,
    title,
    description,
    onTitleChange,
    onDescriptionChange,
}) {
    return (
        <PropsSection title="Question">
            <PropsField
                label="Internal name"
                hint="Not shown to patients. Must be unique."
                error={nameError}
            >
                <div className="input-group input-group-sm">
                    <span className="input-group-text bg-white">
                        <i className="bi bi-eye-slash text-muted" />
                    </span>
                    <input
                        type="text"
                        className={`form-control${nameError ? ' is-invalid' : ''}`}
                        value={localName}
                        onChange={(e) => onNameChange(e.target.value)}
                    />
                </div>
            </PropsField>

            <PropsField label="Question title">
                <input
                    type="text"
                    className="form-control form-control-sm"
                    value={title || ''}
                    onChange={(e) => onTitleChange(e.target.value)}
                />
            </PropsField>

            <PropsField label="Description">
                <textarea
                    className="form-control form-control-sm"
                    rows={2}
                    value={description || ''}
                    onChange={(e) => onDescriptionChange(e.target.value)}
                />
            </PropsField>
        </PropsSection>
    );
}

export function PropsOptionsEditor({ options, onChange }) {
    const list = Array.isArray(options) ? options : [];

    const handleOptionChange = (index, field, value) => {
        const updated = [...list];
        updated[index] = { ...updated[index], [field]: value };
        onChange(updated);
    };

    const addOption = () => {
        onChange([
            ...list,
            {
                id: `opt_${Date.now()}`,
                text: `Option ${list.length + 1}`,
                value: `option_${list.length + 1}`,
            },
        ]);
    };

    const removeOption = (index) => {
        if (list.length <= 1) return;
        onChange(list.filter((_, i) => i !== index));
    };

    return (
        <PropsSection title="Options">
            <div className="ecx-survey-props__options">
                {list.map((option, index) => (
                    <div key={option.id || index} className="ecx-survey-props__option-row">
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder={`Option ${index + 1}`}
                            value={option.text || ''}
                            onChange={(e) => handleOptionChange(index, 'text', e.target.value)}
                        />
                        <input
                            type="text"
                            className="form-control form-control-sm ecx-survey-props__option-value"
                            placeholder="Value"
                            value={option.value || ''}
                            onChange={(e) => handleOptionChange(index, 'value', e.target.value)}
                        />
                        <button
                            type="button"
                            className="ecx-survey-props__option-remove"
                            title="Remove option"
                            disabled={list.length <= 1}
                            onClick={() => removeOption(index)}
                        >
                            <i className="bi bi-trash" />
                        </button>
                    </div>
                ))}
            </div>
            <button
                type="button"
                className="btn btn-sm btn-outline-primary rounded-pill"
                onClick={addOption}
            >
                <i className="bi bi-plus-circle me-1" />
                Add option
            </button>
        </PropsSection>
    );
}

export function PropsRequiredAndMeta({ required, onRequiredChange, questionId }) {
    return (
        <>
            <PropsSection title="Validation">
                <PropsToggle
                    checked={required}
                    onChange={(e) => onRequiredChange(e.target.checked)}
                    label="Required question"
                />
            </PropsSection>
            <div className="ecx-survey-props__meta">
                <span className="ecx-page-chip">ID</span>
                <code>{questionId}</code>
            </div>
        </>
    );
}

export function PropsNotFound() {
    return <div className="ecx-survey-editor__hint text-danger">Question not found</div>;
}

export function PropsShell({ typeLabel, typeIcon, children }) {
    return (
        <div className="ecx-survey-props">
            <div className="ecx-survey-props__header">
                <span className="ecx-survey-props__type">
                    <i className={`bi ${typeIcon}`} />
                    {typeLabel}
                </span>
            </div>
            {children}
        </div>
    );
}
